import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MapPin, Spinner, MagnifyingGlass, X as XIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * FOC-011 · <AddressLookup />
 *
 * Reusable address autocomplete widget. Provider-agnostic — the parent
 * receives a structured address object only. Backend proxy hides the
 * Google Places API key.
 *
 * Contract with parent (via `onSelect`):
 *   {
 *     street, city, province, postalCode, country,
 *     latitude, longitude, formatted
 *   }
 *
 * Behaviour:
 *   - Search fires after 3+ characters (debounced 250 ms).
 *   - Suggestions list supports ↑ ↓ Enter Esc keyboard nav.
 *   - Uses a single per-search session token so Google bills the
 *     autocomplete + details fetch as one session (best practice).
 *   - After selection, calling code decides whether to keep or edit
 *     the individual fields. `<AddressLookup />` never persists.
 *   - Falls back gracefully if the backend proxy is unavailable —
 *     manual entry remains fully functional in the surrounding form.
 */

// Generate a v4-ish session token client-side. Doesn't need to be
// cryptographically strong — Google just needs a stable string for one
// user search-then-select cycle.
function newSessionToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AddressLookup({
  onSelect,
  placeholder = "Start typing an address…",
  testid = "address-lookup",
  autoFocus = false,
  disabled = false,
  regionCodes,      // Optional override, e.g. ["ca", "us"]. Defaults to ["ca"].
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [errored, setErrored] = useState(false);
  const sessionRef = useRef(newSessionToken());
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  const runSearch = useCallback(async (text) => {
    const trimmed = text.trim();
    if (trimmed.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setErrored(false);
    try {
      const r = await api.post("/geocoding/autocomplete", {
        input: trimmed,
        session_token: sessionRef.current,
        region_codes: regionCodes,
      });
      // Race-guard: ignore replies that arrive out of order.
      if (seq !== requestSeq.current) return;
      setSuggestions(r.data?.suggestions || []);
      setActiveIdx(r.data?.suggestions?.length ? 0 : -1);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setSuggestions([]);
      setActiveIdx(-1);
      setErrored(true);
      // FOC-011 · Product Owner directive — log provider errors to the
      // browser console during development so operators can distinguish
      // "no matches" from "provider unavailable".
      const detail = e?.response?.data?.detail || e?.message || "unknown error";
      // eslint-disable-next-line no-console
      console.warn("[AddressLookup] autocomplete failed:", detail, e);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [regionCodes]);

  // Debounced search on every keystroke ≥3 chars.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const chooseSuggestion = async (s) => {
    if (!s) return;
    setOpen(false);
    setLoading(true);
    try {
      const r = await api.post("/geocoding/place-details", {
        place_id: s.place_id,
        session_token: sessionRef.current,
      });
      const d = r.data || {};
      // Deliver the provider-agnostic contract the spec called for.
      onSelect?.({
        street:     d.street || "",
        city:       d.city || "",
        province:   d.province || "",
        postalCode: d.postal_code || "",
        country:    d.country || "",
        latitude:   d.latitude ?? null,
        longitude:  d.longitude ?? null,
        formatted:  d.formatted || s.description,
      });
      // A selection ends the billed session — rotate the token for the
      // NEXT search so it starts fresh.
      sessionRef.current = newSessionToken();
      setQuery("");
      setSuggestions([]);
    } catch (e) {
      setErrored(true);
      const detail = e?.response?.data?.detail || e?.message || "unknown error";
      // eslint-disable-next-line no-console
      console.warn("[AddressLookup] place details failed:", detail, e);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0) chooseSuggestion(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const clear = () => {
    setQuery("");
    setSuggestions([]);
    setActiveIdx(-1);
    inputRef.current?.focus();
  };

  const showDropdown = open && (suggestions.length > 0 || loading || errored);

  return (
    <div className="relative" data-testid={testid}>
      <div className="relative">
        <MagnifyingGlass
          size={14}
          weight="bold"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          ref={inputRef}
          type="text"
          className="w-full rounded-md border border-slate-300 bg-white px-8 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-slate-50 disabled:text-slate-400"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => query.trim().length >= 3 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          data-testid={`${testid}-input`}
          aria-autocomplete="list"
          aria-expanded={showDropdown}
        />
        {loading ? (
          <Spinner
            size={13}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-slate-400"
          />
        ) : query ? (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
            aria-label="Clear address search"
            data-testid={`${testid}-clear`}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <XIcon size={11} weight="bold" />
          </button>
        ) : null}
      </div>

      {showDropdown && (
        <ul
          ref={listRef}
          role="listbox"
          data-testid={`${testid}-list`}
          className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {loading && suggestions.length === 0 && (
            <li className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500">
              <Spinner size={11} className="animate-spin" /> Searching…
            </li>
          )}
          {errored && (
            <li
              data-testid={`${testid}-error`}
              className="px-3 py-2 text-xs text-red-600"
            >
              Address lookup unavailable — please type the address manually.
            </li>
          )}
          {!loading && !errored && suggestions.length === 0 && query.trim().length >= 3 && (
            <li className="px-3 py-2 text-xs text-slate-500">No matches — you can still enter the address manually below.</li>
          )}
          {suggestions.map((s, i) => (
            <li
              key={s.place_id}
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => { e.preventDefault(); chooseSuggestion(s); }}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`${testid}-option-${i}`}
              className={`flex cursor-pointer items-start gap-2 px-3 py-2 text-sm ${
                i === activeIdx ? "bg-slate-100" : "hover:bg-slate-50"
              }`}
            >
              <MapPin size={13} weight="bold" className="mt-0.5 shrink-0 text-slate-400" />
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-900">
                  {s.primary_text || s.description}
                </div>
                {s.secondary_text && (
                  <div className="truncate text-xs text-slate-500">{s.secondary_text}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
